// session.ts — the capture orchestrator behind /scan. It creates the scan,
// runs the frame loop (camera → WASM worker with one frame in flight), streams
// IMU batches into the worker's Kalman filter, uploads every keyframe (full-res
// JPEG + keyframeMeta) in order, re-seeds the coarse splat cloud after every
// 2nd new keyframe and PUTs it, then on Finish starts server refinement while
// following /live so the preview swaps to the server's splats. Before the
// scan is created it fingerprints the camera and, when the phone has a lens
// profile (§14), seeds the tracker with its intrinsics as a prior.
//
// Robustness rules (ARCHITECTURE.md §10): a keyframe's bitmap is JPEG-encoded
// and closed as soon as it is detected (only Blobs wait for the serialized
// upload chain), and tracking pauses while MAX_PENDING_UPLOADS keyframes are
// queued; a frame whose size no longer matches the session (phone rotated) is
// skipped with a "rotate back" hint; once dispose() runs no await resumes
// into creating a scan or a LiveClient; and a failed Finish stays retryable.
//
// Adaptive offload (§15): from Start the camera stream is also recorded
// (recorder.ts) and /live is followed (serverfeed.ts). PerfMonitor (perf.ts)
// watches frame-result throughput; when the phone is too slow (or the user
// forced it) the session switches to server mode for good: no more frames go
// to the worker, its session is freed, the backlog of video chunks and IMU
// samples is uploaded and new ones stream (offload.ts), and the preview,
// tracking badge and stats follow the server's SNAP/DELT, tracking and
// server_tracking messages. Finish then stops the recorder and POSTs
// video/finish instead of /finish. That answers 202 with the scan still
// `capturing` while the server drains the rest of the video (§15.4.1): the
// session sits in phase 'processing' — still showing the server's progress,
// tracking and preview — until /live reports `refining` (or ready/failed).
//
// Video archive (§15.6): in device mode the recording is also tee'd into a
// VideoArchive (archive.ts) right after the scan exists and uploaded to the
// store-only archive routes behind the keyframes (paused while any keyframe
// upload is pending, before and after Finish). Finish stops the recorder so its last chunk lands, then
// finishes the archive in the background — /finish never waits for it — and
// dispose() leaves a running archive to the module registry instead of
// discarding the recording. A switch to server mode closes the archive (the
// server's video/start wipes it and the pipeline stores the video instead).
//
// Live mode (§15.7, SessionOptions.liveSplats, on unless switched off): a
// server-mode video/start asks the server to train splats on its GPU while
// the video streams in (`live: true`); its `live` stats reach onLive and its
// splats arrive as the ordinary server preview. The server-tracked camera
// path goes to onTrail for the preview overlay. Device mode never asks.
//
// Flash lighting (§16.1, opt-in via SessionOptions.lighting): a
// FlashController (flash.ts) toggles the torch and locks exposure at the
// first marker; keyframe metas gain flash / flash_changed_ms / exposure and
// the applied toggles are posted to /flash in both modes (flushed before
// Finish). With the option off nothing of this runs and uploads are unchanged.

import { ApiError, api } from '../api';
import type { KeyframeMeta, MapPointJSON, ProcessingMode, Scan } from '../api';
import type { ProgressMessage } from '../live';
import { parseOSPL } from '../splat/format';
import type { SplatCloud } from '../splat/format';
import { CameraCapture, encodeJpeg } from './camera';
import { FlashController, FlashLogUploader } from './flash';
import { Coverage } from './coverage';
import { frameGate } from './gate';
import type { FrameHold, FrameSize } from './gate';
import { ImuStream, requestImuPermission, screenAngle } from './imu';
import { VideoArchive } from './archive';
import type { ArchiveState } from './archive';
import { ImuLog, ServerOffload } from './offload';
import { ExposureLog } from './exposure';
import { PerfMonitor, round1 } from './perf';
import type { ModeOverride, OffloadDecision } from './perf';
import { hfovDeg, identifyDevice, priorConfig, registration } from './profile';
import type { DeviceInfo, PriorConfig } from './profile';
import { VideoRecorder, browserRecorderFactory } from './recorder';
import { ServerFeed } from './serverfeed';
import type { LiveStats, ServerTrackingStats, TrailSnapshot } from './serverfeed';
import type { UploadStats } from './upload';
import { ScanWorker } from './worker';
import type { FrameResultJSON, ImuSampleJSON } from './worker';

// 'processing': server mode after video/finish, while the server drains the
// video (scan still `capturing`); 'refining' follows when it starts refine.
export type SessionPhase = 'idle' | 'starting' | 'capturing' | 'finishing' | 'processing' | 'refining' | 'ready' | 'failed';

export interface CaptureStats {
  frames: number;
  fps: number;
  trackMs: number;
  keyframes: number;
  uploaded: number;
  pendingUploads: number;
  seeds: number;
  markers: number;
  reprojPx: number;
  quality: number;
  imuFused: boolean;
  imuSamples: number;
  coverage: number;
  hold: string; // why tracking is paused ('' = not paused): rotate back / uploads backlog
  // §15 adaptive offload.
  processing: ProcessingMode;
  cameraFps: number;
  processingFps: number; // windowed frame-result rate (device mode)
  chunksRecorded: number;
  chunksUploaded: number;
  uploadedBytes: number;
  serverFrames: number;
  serverLagS: number;
  videoArchive: ArchiveState | ''; // device-mode archive (§15.6), '' when none
}

export interface SessionOptions {
  override?: ModeOverride; // ?mode=server|device
  forceServer?: boolean; // the "Process on server" toggle
  simulateSlowMs?: number; // ?simulateSlowMs=N (worker results delayed)
  lighting?: boolean; // §16.1 flash lighting capture (the toggle / ?flash=1)
  liveSplats?: boolean; // §15.7 live mode for server-mode uploads (default on; the "Live splats" switch)
}

export interface SessionCallbacks {
  onPhase?: (phase: SessionPhase, scan: Scan | null) => void;
  onFrame?: (result: FrameResultJSON, frameW: number, frameH: number, mapPoints: readonly MapPointJSON[]) => void;
  onStats?: (s: CaptureStats) => void;
  onPreview?: (cloud: SplatCloud, source: 'phone' | 'server') => void;
  onProgress?: (p: ProgressMessage) => void;
  onError?: (message: string) => void;
  onDevice?: (info: DeviceInfo) => void; // lens profile resolved / mismatch flagged (§14)
  onOffload?: (decision: OffloadDecision) => void; // switched to server mode (§15)
  onTracking?: (t: ServerTrackingStats) => void; // server tracker state (server mode)
  onLive?: (s: LiveStats) => void; // live-mode training stats (§15.7)
  onTrail?: (t: TrailSnapshot) => void; // server-tracked camera path (server mode)
  // the server refused compute (server processing, finish, refine) because
  // the sign-in lacks openscan.compute: the page offers the compute sign-in
  onComputeRefused?: () => void;
}

export const RESEED_EVERY_KEYFRAMES = 2;
// SWITCH_UPLOAD_WAIT_MS bounds how long the switch to server mode waits for
// an in-flight phone upload before POSTing video/start.
export const SWITCH_UPLOAD_WAIT_MS = 5000;

export class CaptureSession {
  readonly coverage = new Coverage();
  private camera = new CameraCapture();
  private worker: ScanWorker | null = null;
  private imu: ImuStream | null = null;
  private feed: ServerFeed | null = null;
  private perf: PerfMonitor;
  private recorder: VideoRecorder | null = null;
  private offload: ServerOffload | null = null;
  private archive: VideoArchive | null = null;
  private archiveFinal: Promise<void> | null = null;
  private imuLog = new ImuLog();
  private mode: ProcessingMode = 'device';
  private prior: PriorConfig | null = null;
  private videoSize: FrameSize = { width: 0, height: 0 };
  private scan: Scan | null = null;
  private phase: SessionPhase = 'idle';
  private running = false;
  private rafHandle = 0;
  private uploadChain: Promise<void> = Promise.resolve();
  private pending = 0;
  private keyframesSinceSeed = 0;
  private mapPoints: MapPointJSON[] = [];
  private device: DeviceInfo | null = null;
  private fpsWindow: number[] = [];
  private noRecorderWarned = false;
  private disposed = false;
  private sessionSize: FrameSize | null = null;
  private finishRetryable = false;
  private flash: FlashController | null = null;
  private flashLog: FlashLogUploader | null = null;
  private exposureLog: ExposureLog | null = null;
  private stats: CaptureStats = {
    frames: 0, fps: 0, trackMs: 0, keyframes: 0, uploaded: 0, pendingUploads: 0, seeds: 0,
    markers: 0, reprojPx: 0, quality: 0, imuFused: false, imuSamples: 0, coverage: 0, hold: '',
    processing: 'device', cameraFps: 0, processingFps: 0, chunksRecorded: 0, chunksUploaded: 0, uploadedBytes: 0,
    serverFrames: 0, serverLagS: 0, videoArchive: '',
  };

  constructor(private cb: SessionCallbacks, private opts: SessionOptions = {}) {
    this.perf = new PerfMonitor({ override: opts.override, forced: opts.forceServer });
  }

  get processing(): ProcessingMode {
    return this.mode;
  }

  get offloadDecision(): OffloadDecision | null {
    return this.perf.decision;
  }

  get scanId(): string | null {
    return this.scan?.id ?? null;
  }

  // canRetryFinish is true after a Finish whose server call failed: capture
  // has stopped and the uploads are in, so finish() may be called again.
  get canRetryFinish(): boolean {
    return this.phase === 'failed' && this.finishRetryable && !this.disposed;
  }

  private setPhase(p: SessionPhase): void {
    this.phase = p;
    console.info(`[openscan/session] phase → ${p}${this.disposed ? ' (disposed; not reported)' : ''}`);
    if (!this.disposed) this.cb.onPhase?.(p, this.scan);
  }

  // abandoned logs and reports whether dispose() ran while `where` awaited.
  private abandoned(where: string): boolean {
    if (!this.disposed) return false;
    console.info(`[openscan/session] disposed during ${where}; stopping`);
    return true;
  }

  private setHold(h: FrameHold | null): void {
    const msg = h?.message ?? '';
    if (msg === this.stats.hold) return;
    console.info(`[openscan/session] tracking ${h ? `paused (${h.reason}): ${h.message}` : 'resumed'}`);
    this.stats.hold = msg;
    this.emitStats();
  }

  private emitStats(): void {
    if (this.disposed) return;
    this.stats.pendingUploads = this.pending;
    this.stats.imuFused = this.imu?.fused ?? false;
    this.stats.imuSamples = this.imu?.sampleCount ?? 0;
    this.stats.coverage = this.coverage.fraction();
    this.stats.processing = this.mode;
    this.stats.cameraFps = round1(this.perf.cameraFps);
    this.stats.chunksRecorded = this.recorder?.chunkCount ?? 0;
    const up: Pick<UploadStats, 'chunksUploaded' | 'bytesUploaded'> | null = this.offload?.snapshot ?? this.archive?.snapshot ?? null;
    this.stats.chunksUploaded = up?.chunksUploaded ?? 0;
    this.stats.uploadedBytes = up?.bytesUploaded ?? 0;
    this.stats.videoArchive = this.offload ? '' : (this.archive?.snapshot.state ?? '');
    this.cb.onStats?.({ ...this.stats });
  }

  private reportError(where: string, err: unknown): void {
    const msg = err instanceof Error ? err.message : String(err);
    console.error(`[openscan/session] ${where}: ${msg}`);
    if (this.disposed) return;
    if (err instanceof ApiError && err.code === 'compute_scope_required') {
      // Not a fault to show raw: the sign-in lacks the compute scope, which
      // the page fixes with the compute sign-in.
      console.warn(`[openscan/session] ${where}: the sign-in lacks openscan.compute`);
      this.cb.onComputeRefused?.();
      this.cb.onError?.(`${where}: this sign-in cannot start server processing (it lacks compute access)`);
      return;
    }
    this.cb.onError?.(`${where}: ${msg}`);
  }

  // start must be invoked directly from the Start tap: the IMU permission
  // prompt (iOS) is requested before the first await.
  async start(video: HTMLVideoElement, name?: string): Promise<void> {
    if (this.phase !== 'idle' || this.disposed) return;
    const imuPermission = requestImuPermission();
    this.setPhase('starting');
    const cameraStarted = this.camera.start(video);
    // Forced server mode needs no WASM tracker — unless this browser cannot
    // record video, in which case the phone has to track after all.
    const serverFirst = this.perf.override === 'server' && browserRecorderFactory() !== null;
    if (this.perf.override === 'server' && !serverFirst) {
      this.reportError('server processing unavailable', new Error('this browser cannot record video (no MediaRecorder); tracking on the phone'));
    }
    try {
      const worker = serverFirst ? null : new ScanWorker(undefined, this.opts.simulateSlowMs ?? 0);
      this.worker = worker;
      if (worker) worker.onError = (m) => this.reportError('tracker', m);
      const [, , imuOk] = await Promise.all([cameraStarted, worker?.whenReady(), imuPermission]);
      if (this.abandoned('camera/worker startup')) return this.teardownCapture();
      this.startRecorder();
      // Lens profile (§14): fingerprint the camera at its delivered resolution
      // and start from the stored intrinsics when this phone has a profile.
      this.device = await identifyDevice(this.camera.track, this.camera.videoWidth, this.camera.videoHeight);
      if (this.abandoned('device identification')) return this.teardownCapture();
      this.cb.onDevice?.(this.device);
      const scan = await api.createScan(name || undefined, registration(this.device));
      if (this.abandoned('scan creation')) {
        this.teardownCapture();
        // The page is gone, so nobody will ever finish this scan: remove it.
        void api.deleteScan(scan.id).then(
          () => console.info(`[openscan/session] deleted orphan scan ${scan.id}`),
          (err) => console.warn(`[openscan/session] could not delete orphan scan ${scan.id}`, err),
        );
        return;
      }
      this.scan = scan;
      console.info(`[openscan/session] scan ${scan.id} created`);
      this.follow(scan.id);
      if (worker) this.startArchive(scan.id); // forced server mode uploads through video/start instead
      const probe = this.camera.grabFrame();
      if (!probe) throw new Error('camera produced no frame');
      const prior = this.device.profile ? priorConfig(this.device.profile, probe.width, probe.height) : null;
      const hfov = prior ? hfovDeg(prior.prior_intrinsics.fx) : 65;
      if (prior) {
        console.info(`[openscan/session] starting from lens profile: fx=${(prior.prior_intrinsics.fx * probe.width).toFixed(1)} px (hfov ${hfov.toFixed(1)}°), σf=${(prior.prior_sigma.f_rel * 100).toFixed(2)}%`);
      }
      this.prior = prior;
      this.sessionSize = { width: probe.width, height: probe.height };
      worker?.newSession({ width: probe.width, height: probe.height, hfov_deg: hfov, use_imu: imuOk, ...(prior ?? {}) });
      if (imuOk) {
        this.imu = new ImuStream(this.onImu);
        this.imu.start();
      } else {
        console.info('[openscan/session] IMU unavailable or denied; vision-only tracking');
      }
      this.startFlash(scan.id);
      this.running = true;
      this.setPhase('capturing');
      this.startPerf();
      this.loop();
    } catch (err) {
      if (!this.disposed) this.reportError('start failed', err);
      this.teardownCapture();
      // getUserMedia may resolve after the failure (or after dispose): stop
      // the stream it opens so the camera light does not stay on.
      void cameraStarted.then(() => this.camera.stop(), () => {});
      this.setPhase('failed');
    }
  }

  private loop = (): void => {
    if (!this.running || this.mode === 'server') return; // server mode: the camera only feeds the recorder
    this.rafHandle = requestAnimationFrame(this.loop);
    const decision = this.perf.check(performance.now());
    if (decision) return void this.switchToServer(decision);
    const w = this.worker;
    if (!w || w.busy) return; // backpressure: one frame in flight
    // Upload backlog full: don't even grab, so no new keyframe can be made.
    const backlog = frameGate(null, null, this.pending);
    if (backlog) {
      this.perf.suspend(performance.now()); // withheld frames say nothing about speed
      return this.setHold(backlog);
    }
    const grabbed = this.camera.grab();
    if (!grabbed) return;
    const tMs = performance.now();
    const img = grabbed.image;
    const snap = grabbed.full; // full-res pixels of this very frame (same draw as img)
    const hold = frameGate(img, this.sessionSize, this.pending);
    this.setHold(hold);
    if (hold) {
      // Rotated: the session would reject this size with ErrFrameSize.
      this.perf.suspend(tMs);
      void snap.then((b) => b?.close());
      return;
    }
    const frameW = img.width, frameH = img.height;
    w.processFrame(img, tMs)
      .then((res) => this.handleResult(res, frameW, frameH, tMs, snap))
      .catch((err) => {
        void snap.then((b) => b?.close());
        if (this.running && this.mode === 'device') this.reportError('frame', err);
      });
  };

  private async handleResult(res: FrameResultJSON, frameW: number, frameH: number, tMs: number, snap: Promise<ImageBitmap | null>): Promise<void> {
    const now = performance.now();
    if (this.mode === 'server') {
      // A frame that was in flight at the switch: the server re-tracks it.
      void snap.then((b) => b?.close());
      return;
    }
    this.stats.frames++;
    this.stats.trackMs = Math.round(now - tMs);
    this.fpsWindow.push(now);
    while (this.fpsWindow.length && now - this.fpsWindow[0] > 2000) this.fpsWindow.shift();
    this.stats.fps = this.fpsWindow.length / 2;
    const decision = this.perf.recordResult(now);
    this.stats.processingFps = round1(this.perf.processingFps(now));
    this.stats.markers = res.markers?.length ?? 0;
    if (this.stats.markers > 0) void this.flash?.lockExposure();
    this.stats.reprojPx = res.reproj_err_px;
    this.stats.quality = res.quality;
    if (res.map_points) this.mapPoints = res.map_points;
    if (res.profile_mismatch && this.device && !this.device.mismatch) {
      this.device = { ...this.device, mismatch: true };
      console.warn('[openscan/session] profile_mismatch: the tracker dropped the lens-profile prior and is self-calibrating');
      this.cb.onDevice?.(this.device);
    }
    this.cb.onFrame?.(res, frameW, frameH, this.mapPoints);

    if (res.keyframe && res.have_pose && this.running) {
      this.stats.keyframes++;
      this.coverage.add(res.pose);
      console.info(`[openscan/session] keyframe ${res.keyframe_index} (markers=${this.stats.markers}, q=${res.quality.toFixed(2)}, src=${res.pose_source})`);
      this.enqueueKeyframe(res.keyframe_index, snap);
    } else {
      void snap.then((b) => b?.close());
    }
    if (this.stats.frames % 15 === 0 || res.keyframe) this.emitStats();
    if (decision) void this.switchToServer(decision);
  }

  // enqueueKeyframe JPEG-encodes the keyframe's full-res bitmap right away
  // and closes it, then serializes the Blob upload so the server sees
  // keyframes in order; every RESEED_EVERY_KEYFRAMES uploads it re-seeds and
  // PUTs the seed. `pending` (encoding + queued + uploading) gates the loop.
  private enqueueKeyframe(index: number, snap: Promise<ImageBitmap | null>): void {
    const scanId = this.scan?.id;
    const worker = this.worker;
    if (!scanId || !worker || this.disposed) {
      void snap.then((b) => b?.close());
      return;
    }
    this.pending++;
    const jpeg = (async () => {
      const bitmap = await snap;
      if (!bitmap) throw new Error('no full-resolution frame for keyframe');
      try {
        return await encodeJpeg(bitmap);
      } finally {
        bitmap.close();
      }
    })();
    jpeg.catch(() => {}); // observed in the chain below; avoid an early unhandled rejection
    this.uploadChain = this.uploadChain.then(async () => {
      try {
        const blob = await jpeg;
        if (this.abandoned(`keyframe ${index} upload`)) return;
        if (this.mode === 'server') {
          console.debug(`[openscan/session] keyframe ${index} not uploaded: server mode re-tracks the video`);
          return;
        }
        const meta = this.withLighting(await worker.keyframeMeta(index));
        const t0 = performance.now();
        await api.uploadKeyframe(scanId, meta, blob);
        this.stats.uploaded++;
        console.info(`[openscan/session] uploaded keyframe ${index} (${Math.round(blob.size / 1024)} KiB, ${Math.round(performance.now() - t0)} ms, ${this.pending - 1} still pending)`);
        this.keyframesSinceSeed++;
        if (this.keyframesSinceSeed >= RESEED_EVERY_KEYFRAMES) await this.reseed();
      } catch (err) {
        if (this.mode === 'server') console.debug(`[openscan/session] keyframe ${index} dropped after the switch`, err);
        else this.reportError(`keyframe ${index} upload`, err);
      } finally {
        this.pending--;
        this.archive?.kick(); // the video archive waits for keyframe uploads (§15.6)
        this.emitStats();
      }
    });
  }

  // startFlash starts the exposure log and the opt-in flash lighting
  // capture (§16.1).
  private startFlash(scanId: string): void {
    // The exposure series is kept in every mode (the trajectory solver
    // sizes motion blur from it); it only reads the camera settings.
    this.exposureLog = new ExposureLog(scanId, this.camera.track, () => this.flash?.exposureLocked ?? false, api);
    this.exposureLog.start();
    if (!this.opts.lighting) return;
    const flash = new FlashController(this.camera.track);
    this.flash = flash;
    flash.start();
    this.flashLog = new FlashLogUploader(scanId, flash, api);
    this.flashLog.start();
    void flash.ready.then((caps) => {
      console.info(`[openscan/session] flash lighting on (torch ${caps.torch ? 'toggling' : 'unsupported'}, manual exposure ${caps.exposureManual ? 'available' : 'unavailable'})`);
      // Say so on screen: a silent no-op looks like a broken mode.
      if (!caps.torch && this.flash === flash && this.running) {
        this.reportError('flash lighting', new Error('this browser cannot switch the camera torch, so the scan continues without flash (Chrome on Android supports it)'));
      }
    });
  }

  // stopFlash uploads the rest of the exposure log, turns the torch off and
  // uploads the rest of the toggle log.
  private async stopFlash(): Promise<void> {
    const flash = this.flash, log = this.flashLog;
    // Only wait when samples are queued, so finishing is not delayed for
    // nothing (they must land while the scan is still capturing).
    const exp = this.exposureLog;
    exp?.stop();
    if (exp?.pendingCount) await exp.close();
    if (!flash) return;
    await flash.stop();
    try {
      await log?.close();
    } catch (err) {
      console.warn('[openscan/session] final flash log flush failed', err);
    }
  }

  // withLighting adds the §16.1 tags to a keyframe meta (lighting on only).
  private withLighting(meta: KeyframeMeta): KeyframeMeta {
    const flash = this.flash;
    if (!flash) return meta;
    const out: KeyframeMeta = { ...meta };
    if (flash.torchSupported) {
      const tag = flash.stateAt(meta.timestamp_ms);
      out.flash = tag.flash;
      out.flash_changed_ms = tag.flash_changed_ms;
    }
    const exp = flash.latestExposure();
    if (exp) out.exposure = exp;
    console.debug(`[openscan/session] keyframe ${meta.index} lighting: flash=${out.flash ?? '-'} changed=${out.flash_changed_ms ?? '-'} ms exposure`, exp);
    return out;
  }

  private async reseed(): Promise<void> {
    const scanId = this.scan?.id;
    if (!scanId || !this.worker || this.disposed || this.mode === 'server') return;
    this.keyframesSinceSeed = 0;
    const t0 = performance.now();
    const ospl = await this.worker.seed();
    const cloud = parseOSPL(ospl);
    this.stats.seeds++;
    console.info(`[openscan/session] seed ${cloud.count} gaussians in ${Math.round(performance.now() - t0)} ms`);
    if (this.processing === 'server') {
      // The switch happened while the worker seeded: the server refuses
      // phone uploads now (409) and re-seeds from the video itself.
      console.debug(`[openscan/session] seed of ${cloud.count} gaussians not uploaded: switched to server mode while seeding`);
      return;
    }
    if (!this.disposed) this.cb.onPreview?.(cloud, 'phone');
    await api.putSeed(scanId, ospl);
  }

  // finish stops capture, drains uploads, pushes a final seed (best effort),
  // starts the server refinement and subscribes to live updates. When the
  // POST /finish fails the scan keeps its keyframes and the phase goes to
  // 'failed' with canRetryFinish set; calling finish() again retries only the
  // server call.
  async finish(): Promise<void> {
    const retry = this.canRetryFinish;
    if ((this.phase !== 'capturing' && !retry) || !this.scan || this.disposed) return;
    const id = this.scan.id;
    if (this.mode === 'server') return this.finishServer(id, retry);
    this.setPhase('finishing');
    if (!retry) {
      this.teardownCapture();
      this.setHold(null);
      await this.uploadChain;
      if (this.abandoned('finish: draining uploads')) return;
      if (this.keyframesSinceSeed > 0) {
        try {
          await this.reseed();
        } catch (err) {
          // The server re-seeds from the keyframes anyway; don't strand the scan.
          this.reportError('final seed (continuing)', err);
        }
        if (this.abandoned('finish: final seed')) return;
      }
      await this.stopFlash();
      if (this.abandoned('finish: flash log')) return;
      this.finishRetryable = true;
    } else {
      console.info(`[openscan/session] retrying finish for ${id}`);
    }
    let scan: Scan;
    try {
      scan = await this.finishOnServer(id);
    } catch (err) {
      if (this.abandoned('finish: POST /finish')) return;
      this.reportError('finish failed (retry available)', err);
      this.setPhase('failed');
      return;
    }
    this.finishRetryable = false;
    this.worker?.free();
    if (this.abandoned('finish: POST /finish')) return;
    this.scan = scan;
    this.follow(scan.id);
    this.setPhase('refining');
    this.feed?.show();
  }

  // finishOnServer POSTs /finish. A 409 on a retry can mean the first
  // attempt landed and only its response was lost: then the scan has already
  // left 'capturing' and that counts as success.
  private async finishOnServer(id: string): Promise<Scan> {
    try {
      return await api.finishScan(id);
    } catch (err) {
      if (!(err instanceof ApiError) || err.status !== 409) throw err;
      const s = await api.getScan(id);
      if (s.status === 'capturing') throw err;
      console.info(`[openscan/session] finish: scan ${id} is already ${s.status}`);
      return s;
    }
  }

  // follow opens the /live feed for this scan (once, from creation on).
  private follow(id: string): void {
    if (this.abandoned('follow')) return;
    if (this.feed?.scanId === id) return;
    this.feed?.close();
    this.feed = new ServerFeed(id, {
      // The server cloud is the preview in server mode and after Finish; in
      // device-mode capture the phone's own seeds are.
      showClouds: () => this.mode === 'server' || !(this.phase === 'capturing' || this.phase === 'starting'),
      onStatus: (scan) => this.onServerStatus(scan),
      onProgress: (p) => {
        if (!this.disposed) this.cb.onProgress?.(p);
      },
      onTracking: (t) => this.onServerTracking(t),
      onLive: (l) => {
        if (!this.disposed) this.cb.onLive?.(l);
      },
      onTrail: (t) => {
        if (this.mode === 'server' && !this.disposed) this.cb.onTrail?.(t);
      },
      onCloud: (cloud) => {
        if (!this.disposed) this.cb.onPreview?.(cloud, 'server');
      },
    });
  }

  private onServerStatus(scan: Scan): void {
    this.scan = scan;
    if (scan.status === 'ready') this.setPhase('ready');
    else if (scan.status === 'refining' && this.phase === 'processing') {
      console.info(`[openscan/session] server drained the video of ${scan.id}; refinement started`);
      this.setPhase('refining');
    }
    else if (scan.status === 'failed') {
      if (this.phase === 'capturing') {
        // e.g. the server video pipeline rejected the stream (§15.4).
        this.reportError('server', new Error(scan.error || 'scan failed on the server'));
        this.teardownCapture();
        this.offload?.close(); // also drops the recording
        this.camera.stop();
      } else if (this.phase === 'processing') {
        // the drain after video/finish failed ("server video processing failed: …")
        this.reportError('server', new Error(scan.error || 'server video processing failed'));
      }
      this.setPhase('failed');
    } else if (!this.disposed) this.cb.onPhase?.(this.phase, scan);
  }

  private onServerTracking(t: ServerTrackingStats): void {
    if (this.mode !== 'server' || this.disposed) return;
    this.stats.serverFrames = t.frames;
    this.stats.serverLagS = t.lagS;
    this.stats.keyframes = t.keyframes;
    this.stats.markers = t.markers;
    if (t.markers > 0) void this.flash?.lockExposure();
    this.stats.quality = t.quality;
    this.cb.onTracking?.(t);
    this.emitStats();
  }

  // onImu feeds the WASM filter and logs the samples in device mode; in
  // server mode they go to the uploader (same performance.now() clock).
  private onImu = (samples: ImuSampleJSON[], angle: number): void => {
    if (this.mode === 'server') {
      this.offload?.pushImu(samples, angle);
      return;
    }
    this.worker?.pushIMU(samples, angle);
    this.imuLog.add(samples, angle);
    this.archive?.addImu(samples, angle);
  };

  // startArchive tees the recording into the device-mode video archive
  // (§15.6) and POSTs archive/start in the background.
  private startArchive(scanId: string): void {
    const rec = this.recorder;
    if (!rec?.recording) {
      console.info('[openscan/session] no recording: the scan will have no archived video');
      return;
    }
    const archive = new VideoArchive(scanId, api, {
      paused: () => this.mode === 'device' && this.pending > 0, // keyframes first, also while Finish drains them
      onStats: () => this.emitStats(),
    });
    this.archive = archive;
    rec.tee((c) => archive.addChunk(c));
    const startMs = rec.startMs ?? performance.now();
    if (rec.startMs === null) console.warn('[openscan/session] recorder onstart has not fired; archiving with now as recorder_start_ms');
    void archive
      .begin({
        mime: rec.mime, recorder_start_ms: startMs, width: this.videoSize.width, height: this.videoSize.height,
        bits_per_second: rec.bitsPerSecond, screen_angle: screenAngle(),
        ...(this.device?.fingerprint.key ? { device_key: this.device.fingerprint.key } : {}),
      })
      .catch((err) => console.warn('[openscan/session] video archive did not start (the scan keeps no video)', err));
  }

  // closeArchive abandons the device-mode archive (switch to server mode).
  private closeArchive(why: string): void {
    if (!this.archive) return;
    console.info(`[openscan/session] closing the video archive: ${why}`);
    this.recorder?.tee(null);
    this.archive.close();
    this.archive = null;
  }

  // finalizeRecording ends a device-mode recording: without an archive it is
  // discarded; with one the recorder is stopped (its final chunk reaches the
  // archive through the tee), its switch buffer freed, and the archive
  // finished in the background. Idempotent; never awaited by Finish.
  private finalizeRecording(): void {
    const rec = this.recorder, archive = this.archive;
    if (this.archiveFinal) return;
    if (!rec || !archive) {
      rec?.discard();
      return;
    }
    this.archiveFinal = (async () => {
      await rec.stop();
      rec.discard(); // the archive still holds the unsent Blobs
      try {
        const v = await archive.finish(rec.lastSeq);
        console.info(`[openscan/session] video archive ${v?.state ?? 'dropped'}: ${v?.bytes ?? 0} B in ${v?.chunks ?? 0} chunks`);
      } catch (err) {
        console.warn('[openscan/session] video archive did not finish (the scan keeps a partial or no video)', err);
      }
      this.emitStats();
    })();
  }

  // startRecorder records the camera stream from Start, whatever the mode.
  private startRecorder(): void {
    const stream = this.camera.mediaStream;
    this.videoSize = { width: this.camera.videoWidth, height: this.camera.videoHeight };
    if (!stream) return;
    const rec = new VideoRecorder(stream, this.videoSize.width, this.videoSize.height);
    rec.onError = (m) => this.reportError('recorder', m);
    if (rec.start()) this.recorder = rec;
    else console.info('[openscan/session] no video recording: server offload unavailable');
  }

  // startPerf starts the throughput monitor and measures the camera rate.
  private startPerf(): void {
    const settings = this.camera.settingsFps;
    this.perf.setCameraFps(settings, null);
    void this.camera.measureFps().then((measured) => {
      if (this.disposed) return;
      this.perf.setCameraFps(settings, measured);
      this.emitStats();
    });
    const decision = this.perf.start(performance.now());
    if (decision) void this.switchToServer(decision);
  }

  // switchToServer hands the scan to the server for good (§15.3).
  private async switchToServer(decision: OffloadDecision): Promise<void> {
    if (this.mode === 'server' || this.disposed || !this.scan || !this.running) return;
    const rec = this.recorder;
    if (!rec?.recording) {
      if (!this.noRecorderWarned) {
        this.noRecorderWarned = true;
        console.warn(`[openscan/session] would switch to server mode (${decision.reason}) but nothing is being recorded; staying on the phone`);
      }
      return;
    }
    this.mode = 'server';
    console.info(`[openscan/session] server mode: ${decision.reason} (camera ${decision.camera_fps} fps, processing ${decision.processing_fps} fps)`);
    cancelAnimationFrame(this.rafHandle);
    this.worker?.free(); // the server re-tracks everything; release the WASM session
    this.setHold(null);
    this.stats.keyframes = 0; // phone keyframes are discarded server-side
    this.stats.processingFps = decision.processing_fps;
    this.closeArchive('switched to server mode');
    this.cb.onOffload?.(decision);
    this.offload = new ServerOffload(
      {
        scanId: this.scan.id, decision, recorder: rec, width: this.videoSize.width, height: this.videoSize.height,
        deviceKey: this.device?.fingerprint.key, prior: this.prior, screenAngle: screenAngle(),
        live: this.opts.liveSplats ?? true,
      },
      api,
      { onStats: () => this.emitStats(), onError: (m) => this.reportError('video upload', m) },
    );
    this.feed?.show();
    this.emitStats();
    // A phone keyframe/seed upload already on the wire would be refused
    // (409) once video/start lands; let it finish first. Recording goes on
    // meanwhile and its chunks wait in the recorder's buffer.
    await this.settleUploads(SWITCH_UPLOAD_WAIT_MS);
    if (this.abandoned('switch: waiting for phone uploads')) return;
    try {
      await this.offload.begin(this.imuLog.drain(), this.imuLog.angle);
    } catch (err) {
      this.reportError('server processing could not start (Finish retries it)', err);
    }
  }

  // settleUploads waits (at most ms) for the in-flight phone upload chain.
  private async settleUploads(ms: number): Promise<void> {
    if (this.pending === 0) return;
    const t0 = performance.now();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<'timeout'>((r) => (timer = setTimeout(() => r('timeout'), ms)));
    const done = await Promise.race([this.uploadChain.then(() => 'done' as const, () => 'done' as const), timeout]);
    clearTimeout(timer);
    console.info(`[openscan/session] switch: phone uploads ${done === 'done' ? 'settled' : 'still pending (giving up waiting)'} after ${Math.round(performance.now() - t0)} ms`);
  }

  // finishServer: stop the recorder, upload the rest, POST video/finish. A
  // failure while every chunk is accounted for stays retryable.
  private async finishServer(id: string, retry: boolean): Promise<void> {
    const off = this.offload;
    if (!off) return;
    this.setPhase('finishing');
    if (!retry) {
      this.running = false;
      this.imu?.stop(); // flushes its last batch into the uploader
      await this.stopFlash();
      if (this.abandoned('finish: flash log')) return;
    } else {
      console.info(`[openscan/session] retrying video/finish for ${id}`);
    }
    let scan: Scan | null;
    try {
      scan = await off.finish();
    } catch (err) {
      if (this.abandoned('finish: video upload')) return;
      const landed = await this.finishLanded(id, err);
      if (this.abandoned('finish: video/finish check')) return;
      if (landed) return this.afterVideoFinish(landed);
      this.finishRetryable = off.retryable;
      this.reportError(`finish failed${this.finishRetryable ? ' (retry available)' : ''}`, err);
      this.setPhase('failed');
      return;
    }
    if (this.abandoned('finish: video/finish')) return;
    this.afterVideoFinish(scan);
  }

  // afterVideoFinish: video/finish was accepted (202). The reply's scan is
  // normally still `capturing` (the server drains the video first); /live
  // may already have reported a later status, which then wins.
  private afterVideoFinish(reply: Scan | null): void {
    this.finishRetryable = false;
    this.camera.stop();
    const known = this.scan && this.scan.status !== 'capturing' ? this.scan : null;
    const scan = known ?? reply ?? this.scan;
    if (scan) this.scan = scan;
    const status = scan?.status ?? 'capturing';
    console.info(`[openscan/session] video/finish accepted for ${scan?.id ?? '?'}: scan ${status}${known ? ' (from /live)' : ''}`);
    if (status === 'ready') this.setPhase('ready');
    else if (status === 'refining') this.setPhase('refining');
    else if (status === 'failed') {
      this.reportError('server', new Error(scan?.error || 'scan failed on the server'));
      this.setPhase('failed');
    } else this.setPhase('processing');
    this.feed?.show();
  }

  // finishLanded: a video/finish that answered 409 may be a retry of one
  // that landed while only its response was lost — then the pipeline is
  // gone and the scan has left `capturing`. Returns that scan, else null.
  private async finishLanded(id: string, err: unknown): Promise<Scan | null> {
    if (!(err instanceof ApiError) || err.status !== 409) return null;
    try {
      const s = await api.getScan(id);
      if (s.status === 'refining' || s.status === 'ready') {
        console.info(`[openscan/session] video/finish 409 but scan ${id} is already ${s.status}: the finish had landed`);
        return s;
      }
      console.warn(`[openscan/session] video/finish 409 and scan ${id} is ${s.status}`);
    } catch (e) {
      console.warn('[openscan/session] video/finish 409; could not re-read the scan', e);
    }
    return null;
  }

  // teardownCapture stops tracking input. In device mode the recording goes
  // to the video archive (or is dropped without one); a server-mode upload
  // still needs it (finishServer stops it itself).
  private teardownCapture(): void {
    this.running = false;
    cancelAnimationFrame(this.rafHandle);
    this.imu?.stop();
    void this.flash?.stop(); // the torch must never be left on
    if (this.mode === 'device') {
      this.finalizeRecording(); // before camera.stop(): the recorder's last chunk still lands
      this.camera.stop();
    }
  }

  dispose(): void {
    this.disposed = true;
    this.teardownCapture();
    void this.flashLog?.close();
    void this.exposureLog?.close();
    this.offload?.close();
    this.offload = null;
    // A device-mode archive keeps uploading from the registry (finalizeRecording
    // discards the recorder once its last chunk is in the archive).
    if (!this.archiveFinal) this.recorder?.discard();
    this.camera.stop();
    this.feed?.close();
    this.feed = null;
    this.worker?.terminate();
    this.worker = null;
    console.info('[openscan/session] disposed');
  }
}
